import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraInput, SpecraLink, SpecraQuote, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraPageHeader, SpecraTooltip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { rfiSectionClient } from '../rfiSectionHooks';
import { useRfiRecord } from './RfiRecordContext';
import { useRfiWorkspaceHome } from './useRfiWorkspaceHome';
const T = Testids.rfiWorkspace;
const LCAT_KEY = 'rfi-labor-categories';
const MATCH_BADGE = {
    equivalent: {
        label: 'Equivalent',
        variant: 'success'
    },
    broader: {
        label: 'Broader',
        variant: 'info'
    },
    narrower: {
        label: 'Narrower',
        variant: 'violet'
    },
    candidate: {
        label: 'Candidate',
        variant: 'warning'
    }
};
/** Match column label: entry title · vehicle name, read from the registry at list time (no rate, D5). */ export function lcatMatchLabel(item) {
    const m = item.match;
    if (!m || item.matchState === 'unmatched') return 'Unmatched';
    if (m.matchStatus === 'VEHICLE_MISMATCH' && m.vehicleName) return `Only on ${m.vehicleName}`;
    return [
        m.entryTitle,
        m.vehicleName
    ].filter(Boolean).join(' · ') || 'Matched';
}
/** "One title per line" → titles (blank lines and duplicates dropped). */ export function parseLcatPaste(text) {
    const seen = new Set();
    const out = [];
    for (const line of text.split(/\r?\n/)){
        const t = line.replace(/^[-*•\d.)\s]+/, '').trim();
        if (t && !seen.has(t.toLowerCase())) {
            seen.add(t.toLowerCase());
            out.push(t);
        }
    }
    return out;
}
function useLcats(rfiId) {
    return useQuery({
        queryKey: [
            LCAT_KEY,
            rfiId
        ],
        enabled: Boolean(rfiId),
        queryFn: ()=>rfiSectionClient.listRfiLaborCategories({
                rfiResponseProjectId: rfiId
            })
    });
}
function useLcatMutations(rfiId) {
    const qc = useQueryClient();
    const done = ()=>Promise.all([
            qc.invalidateQueries({
                queryKey: [
                    LCAT_KEY,
                    rfiId
                ]
            }),
            qc.invalidateQueries({
                queryKey: [
                    'rfi-nav-counts',
                    rfiId
                ]
            })
        ]);
    return {
        create: useMutation({
            mutationFn: (items)=>rfiSectionClient.createRfiLaborCategories({
                    rfiResponseProjectId: rfiId,
                    items
                }),
            onSuccess: done
        }),
        remove: useMutation({
            mutationFn: (id)=>rfiSectionClient.removeRfiLaborCategory({
                    rfiResponseProjectId: rfiId,
                    id
                }),
            onSuccess: done
        }),
        match: useMutation({
            mutationFn: async (ids)=>{
                for (const id of ids){
                    await rfiSectionClient.updateRfiLaborCategoryMatch({
                        rfiResponseProjectId: rfiId,
                        id
                    });
                }
            },
            onSuccess: done
        })
    };
}
/**
 * Labor categories (WRT-07 page 5): what the RFI names, verbatim, with the
 * REG-17 rate-book match as registry links. No rate column (D5). WRT-33
 * mounts its proposals in `proposedSlot`.
 */ export function RfiLaborCategoriesPage({ proposedSlot = null }) {
    const { id = '' } = useParams();
    const record = useRfiRecord();
    const rfiId = record?.pangaeaId ?? '';
    const { data, isLoading } = useLcats(rfiId);
    const { data: home } = useRfiWorkspaceHome(rfiId);
    const questionRows = (home?.questions ?? []).filter((q)=>q.questionId);
    const { create, remove, match } = useLcatMutations(rfiId);
    const [adding, setAdding] = useState(false);
    const [pasting, setPasting] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const items = data?.items ?? [];
    const questionByID = new Map(questionRows.map((q)=>[
            q.questionId,
            q
        ]));
    const matchDisabled = !data?.matcherAvailable ? 'Arrives with the registry matcher' : !data?.effectiveVehicleId ? "Set the RFI's vehicle first" : '';
    const matchButton = /*#__PURE__*/ _jsx(SpecraButton, {
        "data-testid": T.lcatMatch,
        disabled: Boolean(matchDisabled) || items.length === 0 || match.isPending,
        onClick: ()=>match.mutate(items.map((i)=>i.id)),
        size: "sm",
        variant: "secondary",
        children: "Match with rate book"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-4)] p-[var(--space-6)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                actions: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.lcatAdd,
                            onClick: ()=>setAdding(true),
                            size: "sm",
                            children: "Add labor category"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.lcatPaste,
                            onClick: ()=>setPasting(true),
                            size: "sm",
                            variant: "secondary",
                            children: "Paste a list"
                        }),
                        matchDisabled ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: matchDisabled,
                            children: /*#__PURE__*/ _jsx("span", {
                                children: matchButton
                            })
                        }) : matchButton
                    ]
                }),
                title: "Labor categories",
                titleSize: "ui"
            }),
            proposedSlot,
            match.error || create.error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: (match.error ?? create.error)?.message
            }) : null,
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                columns: [
                    {
                        id: 'title',
                        header: 'As written',
                        cell: (r)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>setExpanded(expanded === r.id ? null : r.id),
                                size: "inline",
                                variant: "link",
                                children: r.asWrittenTitle
                            })
                    },
                    {
                        id: 'qty',
                        header: 'Quantity',
                        cell: (r)=>r.quantityText || '—'
                    },
                    {
                        id: 'source',
                        header: 'Source',
                        cell: (r)=>{
                            const q = questionByID.get(r.sourceQuestionId);
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-[var(--space-1)]",
                                children: [
                                    q ? /*#__PURE__*/ _jsxs(SpecraLink, {
                                        href: `/rfi-response/${id}/questions/${q.sectionId}`,
                                        renderLink: renderRouterLink,
                                        children: [
                                            "Question ",
                                            q.number
                                        ]
                                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: "—"
                                    }),
                                    expanded === r.id && r.sourceQuote ? /*#__PURE__*/ _jsx(SpecraQuote, {
                                        size: "sm",
                                        children: r.sourceQuote
                                    }) : null
                                ]
                            });
                        }
                    },
                    {
                        id: 'match',
                        header: 'Match',
                        cell: (r)=>{
                            const b = MATCH_BADGE[r.match?.matchKind ?? ''] ?? {
                                label: 'Unmatched',
                                variant: 'neutral'
                            };
                            const label = lcatMatchLabel(r);
                            return /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex flex-wrap items-center gap-[var(--space-1-5)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: b.variant,
                                        children: b.label
                                    }),
                                    r.match?.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                                        href: r.match.href,
                                        renderLink: renderRouterLink,
                                        children: label
                                    }) : r.matchState !== 'unmatched' ? /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        children: label
                                    }) : null
                                ]
                            });
                        }
                    }
                ],
                emptyDescription: "Add the labor categories the RFI names, or paste a list.",
                emptyTitle: "No labor categories yet",
                getRowId: (r)=>r.id,
                getRowLabel: (r)=>r.asWrittenTitle,
                loading: isLoading,
                rowActions: (r)=>/*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>remove.mutate(r.id),
                        size: "xs",
                        variant: "ghost",
                        children: "Remove"
                    }),
                rows: items,
                testId: T.lcatTable
            }),
            adding ? /*#__PURE__*/ _jsx(AddLcatDialog, {
                onClose: ()=>setAdding(false),
                onSubmit: (item)=>create.mutate([
                        item
                    ], {
                        onSuccess: ()=>setAdding(false)
                    }),
                questions: questionRows.map((q)=>({
                        id: q.questionId,
                        label: `Question ${q.number}`
                    }))
            }) : null,
            pasting ? /*#__PURE__*/ _jsx(PasteLcatsDialog, {
                onClose: ()=>setPasting(false),
                onSubmit: (titles)=>create.mutate(titles.map((asWrittenTitle)=>({
                            asWrittenTitle
                        })), {
                        onSuccess: ()=>setPasting(false)
                    })
            }) : null
        ]
    });
}
function AddLcatDialog({ questions, onSubmit, onClose }) {
    const [title, setTitle] = useState('');
    const [qty, setQty] = useState('');
    const [question, setQuestion] = useState('');
    const [quote, setQuote] = useState('');
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        onOpenChange: (o)=>!o && onClose(),
        open: true,
        size: "md",
        title: "Add labor category",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-3)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "lcat-title",
                    label: "As written",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "lcat-title",
                        maxLength: 300,
                        onChange: (e)=>setTitle(e.target.value),
                        value: title
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "lcat-qty",
                    label: "Quantity",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "lcat-qty",
                        onChange: (e)=>setQty(e.target.value),
                        placeholder: "2 FTE",
                        value: qty
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "lcat-question",
                    label: "Source question",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        id: "lcat-question",
                        onChange: (e)=>setQuestion(e.target.value),
                        value: question,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "None"
                            }),
                            questions.map((q)=>/*#__PURE__*/ _jsx("option", {
                                    value: q.id,
                                    children: q.label
                                }, q.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "lcat-quote",
                    label: "Quote",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "lcat-quote",
                        onChange: (e)=>setQuote(e.target.value),
                        rows: 3,
                        value: quote
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: onClose,
                            size: "sm",
                            variant: "secondary",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: !title.trim(),
                            onClick: ()=>onSubmit({
                                    asWrittenTitle: title.trim(),
                                    quantityText: qty,
                                    sourceQuestionId: question,
                                    sourceQuote: quote
                                }),
                            size: "sm",
                            children: "Add"
                        })
                    ]
                })
            ]
        })
    });
}
function PasteLcatsDialog({ onSubmit, onClose }) {
    const [text, setText] = useState('');
    const titles = parseLcatPaste(text);
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        onOpenChange: (o)=>!o && onClose(),
        open: true,
        size: "md",
        title: "Paste a list",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-3)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    hint: "One labor category per line.",
                    htmlFor: "lcat-paste",
                    label: "Labor categories",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "lcat-paste",
                        onChange: (e)=>setText(e.target.value),
                        rows: 8,
                        value: text
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: onClose,
                            size: "sm",
                            variant: "secondary",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsxs(SpecraButton, {
                            disabled: titles.length === 0,
                            onClick: ()=>onSubmit(titles),
                            size: "sm",
                            children: [
                                "Add ",
                                titles.length || ''
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
